// Builds of a schedule (decision 036): the site's program, its grid and every info screen are one published program filtered and
// laid out by parameters — which days, rooms and tracks, talks only or everything, speakers, partners, gaps shown as breaks.

import type { BlockParam } from '@confdeck/core-shared';
import type { PublishedSlot } from './program-slots.ts';
import { hhmm, PAUSE_KINDS as PAUSES, toMin } from './schedule-time.ts';

/** Colours a custom slot may take: the palette of the tokens (`--c-<name>-dot`). */
export const SLOT_COLORS = ['blue', 'violet', 'teal', 'green', 'amber', 'red', 'pink', 'gray'] as const;
/** Icons of custom slots: plain characters, so the TV, the site and the editor show the same thing without an icon font. */
export const SLOT_ICONS: Record<string, { glyph: string; label: string }> = {
  coffee: { glyph: '☕', label: 'Кофе' }, lunch: { glyph: '🍽', label: 'Обед' }, mic: { glyph: '🎤', label: 'Микрофон' }, star: { glyph: '★', label: 'Звезда' },
  handshake: { glyph: '🤝', label: 'Партнёры' }, info: { glyph: 'ℹ', label: 'Информация' }, music: { glyph: '♪', label: 'Музыка' }, flag: { glyph: '⚑', label: 'Флаг' },
};

export interface BuildParams {
  /** Days «ГГГГ-ММ-ДД»; empty — every day. */
  days?: string[];
  /** Rooms by term id or name; empty — every room. */
  rooms?: string[];
  /** Tracks by term id or name; empty — every track. */
  tracks?: string[];
  /** Talks only: breaks, partner and custom blocks are left out. */
  talksOnly?: boolean;
  speakers?: boolean;
  partners?: boolean;
  /** A gap in a room longer than this many minutes is shown as «Перерыв»; 0 — gaps stay empty. */
  gapBreak?: number;
}

export interface BuildItem {
  date: string; start: string; end: string; room: string; room_term?: string; track?: string;
  title: string; subtitle: string; kind: string; color?: string; icon?: string; talk?: string;
  speakers: string[]; partners: string[];
  /** A gap of the room shown as a break: not a slot of the program. */
  gap?: boolean;
}

const matches = (list: string[] | undefined, id: string | undefined, name: string | undefined) => !list?.length || list.some((x) => x === id || x === name);
export const isTalk = (s: { kind: string | null; talk?: string; speakers: string[] }) => !!s.talk || s.kind === 'talk' || s.speakers.length > 0;

/** The slots of a build: filtered by days, rooms, tracks and kind; speakers and partners dropped when the build hides them. */
export function pickSlots(slots: PublishedSlot[], p: BuildParams = {}): PublishedSlot[] {
  return slots.filter((s) => !Number.isNaN(toMin(s.start)) && (!p.days?.length || p.days.includes(s.date))
    && matches(p.rooms, s.room_term, s.room) && matches(p.tracks, s.track_term, s.track) && (!p.talksOnly || isTalk(s)))
    .map((s) => ({ ...s, speakers: p.speakers === false ? [] : s.speakers, partners: p.partners === false ? [] : s.partners ?? [] }));
}

/** The items of a build in time order (day, start, room); every source of the program uses it. */
export function buildItems(slots: PublishedSlot[], p: BuildParams = {}): BuildItem[] {
  const picked = pickSlots(slots, p);
  const items: BuildItem[] = picked.map((s) => {
    const start = toMin(s.start);
    return {
      date: s.date, start: hhmm(start), end: hhmm(start + (s.duration > 0 ? s.duration : 40)), room: s.room, ...(s.room_term ? { room_term: s.room_term } : {}), ...(s.track ? { track: s.track } : {}),
      title: s.title, subtitle: s.subtitle ?? '', kind: isTalk(s) ? 'talk' : s.kind ?? 'custom', ...(s.color ? { color: s.color } : {}), ...(s.icon ? { icon: s.icon } : {}),
      ...(s.talk ? { talk: s.talk } : {}), speakers: s.speakers, partners: s.partners ?? [],
    };
  });
  if (p.gapBreak && p.gapBreak > 0) {
    const lanes = new Map<string, BuildItem[]>();
    for (const it of items) { const k = `${it.date}|${it.room}`; (lanes.get(k) ?? lanes.set(k, []).get(k)!).push(it); }
    for (const lane of lanes.values()) {
      lane.sort((a, b) => toMin(a.start) - toMin(b.start));
      for (let i = 1; i < lane.length; i++) {
        const a = toMin(lane[i - 1]!.end), b = toMin(lane[i]!.start);
        // A gap next to a real pause is a part of it, not a second «Перерыв» in a row.
        if (PAUSES.includes(lane[i - 1]!.kind) || PAUSES.includes(lane[i]!.kind)) continue;
        if (b - a > p.gapBreak) items.push({ date: lane[i]!.date, start: hhmm(a), end: hhmm(b), room: lane[i]!.room, ...(lane[i]!.room_term ? { room_term: lane[i]!.room_term } : {}), title: b - a >= 40 ? 'Большой перерыв' : 'Перерыв', subtitle: '', kind: 'break', speakers: [], partners: [], gap: true });
      }
    }
  }
  return items.sort((a, b) => a.date.localeCompare(b.date) || toMin(a.start) - toMin(b.start) || a.room.localeCompare(b.room, 'ru'));
}

/** What a screen shows at `now`: the item that has just ended, the current one and the next ones of one room. */
export function nowNext(items: BuildItem[], now: number, next = 3): { past?: BuildItem; current?: BuildItem; next: BuildItem[] } {
  const current = items.find((b) => toMin(b.start) <= now && now < toMin(b.end));
  return { past: items.filter((b) => toMin(b.end) <= now).at(-1), current, next: items.filter((b) => toMin(b.start) > now).slice(0, next) };
}

/** Comma-separated values of a site block parameter, as a list. */
export const listParam = (v: unknown): string[] => String(v ?? '').split(',').map((x) => x.trim()).filter(Boolean);

/** The parameters of a build as a site block offers them: the program block and the grid by rooms take the same ones. */
export const BUILD_BLOCK_PARAMS: BlockParam[] = [
  { key: 'days', label: 'Дни (ГГГГ-ММ-ДД через запятую)', kind: 'text', help: 'Пусто — все дни' },
  { key: 'rooms', label: 'Залы (через запятую)', kind: 'text', help: 'Пусто — все залы' },
  { key: 'tracks', label: 'Треки (через запятую)', kind: 'text', help: 'Пусто — все треки' },
  { key: 'talks_only', label: 'Только доклады', kind: 'checkbox', default: false },
  { key: 'speakers', label: 'Показывать докладчиков', kind: 'checkbox', default: true },
  { key: 'partners', label: 'Показывать партнёров блоков', kind: 'checkbox', default: true },
];

/** Build parameters from a site block's values; the old «День» parameter of the grid still counts. */
export function blockBuild(p: Record<string, unknown>): BuildParams {
  return { days: [...listParam(p.days), ...listParam(p.day)], rooms: listParam(p.rooms), tracks: listParam(p.tracks), talksOnly: p.talks_only === true, speakers: p.speakers !== false, partners: p.partners !== false };
}
